/**
 * ManualCodeEntry Component (Story 7.3)
 *
 * Manual 4-character coupon code entry for redemption.
 * Pattern: [0-9][A-Z][0-9][A-Z] (e.g., 2F4A)
 *
 * Features:
 * - 4 individual character input boxes
 * - Auto-focus and auto-advance
 * - Alphanumeric keyboard (letters auto-uppercase)
 * - Clear error states
 * - Loading indicator during validation
 * - Paste support
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Loader, AlertCircle, Keyboard } from 'lucide-react';
import { useTranslation } from '@/i18n';

interface ManualCodeEntryProps {
	onSubmit: (code: string) => void;
	isProcessing?: boolean;
	error?: string | null;
	onClearError?: () => void;
}

// Pattern: [0-9][A-Z][0-9][A-Z]
const CHAR_PATTERNS = [/[0-9]/, /[A-Z]/, /[0-9]/, /[A-Z]/];

const ManualCodeEntry: React.FC<ManualCodeEntryProps> = ({
	onSubmit,
	isProcessing = false,
	error = null,
	onClearError,
}) => {
	const { t } = useTranslation();
	const [chars, setChars] = useState<string[]>(['', '', '', '']);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	// Focus first input on mount
	useEffect(() => {
		inputRefs.current[0]?.focus();
	}, []);

	const handleCharChange = useCallback(
		(index: number, value: string) => {
			// Normalize to uppercase
			const char = value.toUpperCase().slice(-1);

			// Validate character matches expected pattern for this position
			if (char && !CHAR_PATTERNS[index]?.test(char)) {
				// Invalid character for this position, ignore
				return;
			}

			const newChars = [...chars];
			newChars[index] = char;
			setChars(newChars);
			onClearError?.();

			// Auto-advance to next input
			if (char && index < 3) {
				inputRefs.current[index + 1]?.focus();
			}

			// Auto-submit when all 4 characters entered
			if (char && index === 3) {
				const code = newChars.join('');
				if (code.length === 4) {
					onSubmit(code);
				}
			}
		},
		[chars, onSubmit, onClearError]
	);

	const handleKeyDown = useCallback(
		(index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
			// Handle backspace - go to previous input
			if (e.key === 'Backspace' && !chars[index] && index > 0) {
				inputRefs.current[index - 1]?.focus();
			}
		},
		[chars]
	);

	const handlePaste = useCallback(
		(e: React.ClipboardEvent) => {
			e.preventDefault();
			const pastedData = e.clipboardData.getData('text').toUpperCase().slice(0, 4);

			// Validate paste matches pattern
			if (pastedData.length === 4) {
				const isValid = pastedData.split('').every((char, index) => CHAR_PATTERNS[index]?.test(char));

				if (isValid) {
					const newChars = pastedData.split('');
					setChars(newChars);
					onClearError?.();
					onSubmit(pastedData);
				}
			}
		},
		[onSubmit, onClearError]
	);

	const handleFormSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		const code = chars.join('');
		if (code.length === 4) {
			onSubmit(code);
		}
	};

	// Reset form when error occurs
	useEffect(() => {
		if (error) {
			// Small delay to show error before resetting
			const timer = setTimeout(() => {
				setChars(['', '', '', '']);
				inputRefs.current[0]?.focus();
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [error]);

	return (
		<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8'>
			<div className='text-center max-w-md mx-auto'>
				{/* Header */}
				<div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4'>
					<Keyboard className='w-8 h-8 text-blue-600' />
				</div>
				<h2 className='text-xl sm:text-2xl font-semibold text-gray-900 mb-2'>
					{t('merchant.manualEntry.title')}
				</h2>
				<p className='text-gray-600 mb-6'>{t('merchant.manualEntry.subtitle')}</p>

				{/* Code Entry Form */}
				<form onSubmit={handleFormSubmit}>
					<div className='flex justify-center gap-3 sm:gap-4 mb-4'>
						{chars.map((char, index) => (
							<div key={index} className='flex flex-col items-center'>
								<input
									ref={el => {
										inputRefs.current[index] = el;
									}}
									type='text'
									inputMode={index % 2 === 0 ? 'numeric' : 'text'}
									maxLength={1}
									value={char}
									onChange={e => handleCharChange(index, e.target.value)}
									onKeyDown={e => handleKeyDown(index, e)}
									onPaste={index === 0 ? handlePaste : undefined}
									disabled={isProcessing}
									className={`
										w-14 h-16 sm:w-16 sm:h-20
										text-center text-2xl sm:text-3xl font-bold uppercase
										border-2 rounded-lg
										focus:ring-2 focus:ring-blue-500 focus:border-blue-500
										disabled:bg-gray-100 disabled:cursor-not-allowed
										transition-colors
										${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}
									`}
									aria-label={t('merchant.manualEntry.charLabel', { number: index + 1 })}
									placeholder={index % 2 === 0 ? '0' : 'A'}
								/>
								<span className='text-xs text-gray-400 mt-1'>
									{index % 2 === 0 ? '0-9' : 'A-Z'}
								</span>
							</div>
						))}
					</div>

					{/* Error Message */}
					{error && (
						<div className='flex items-center justify-center gap-2 text-red-600 mb-4'>
							<AlertCircle className='w-5 h-5' />
							<span className='text-sm font-medium'>{error}</span>
						</div>
					)}

					{/* Loading State */}
					{isProcessing && (
						<div className='flex items-center justify-center gap-2 text-blue-600 mb-4'>
							<Loader className='w-5 h-5 animate-spin' />
							<span className='text-sm'>{t('merchant.manualEntry.validating')}</span>
						</div>
					)}

					{/* Submit Button */}
					<button
						type='submit'
						disabled={chars.join('').length !== 4 || isProcessing}
						className='w-full sm:w-auto px-8 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
					>
						{isProcessing ? t('merchant.manualEntry.validating') : t('merchant.manualEntry.submit')}
					</button>
				</form>

				{/* Help Text */}
				<p className='text-xs text-gray-500 mt-4'>{t('merchant.manualEntry.helpText')}</p>
			</div>
		</div>
	);
};

export default ManualCodeEntry;
